import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Heading,
  Text,
  Image,
  Container,
  Stack,
  Spinner,
  Flex,
  Badge,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";

export default function NewsDetail() {
  const colors = {
    teal: "#1da179",
    tealLight: "#34c49b",
    grayBg: "#f9f9f9",
  };

  const { id } = useParams();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const { i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const res = await axios.get(`https://actpolymers-spring-26d90d713ec5.herokuapp.com/api/news/${id}`);
        setNews(res.data);
      } catch (err) {
        console.error("Failed to fetch news detail", err);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, [id]);

  if (loading) {
    return (
      <Flex py={20} justify="center" align="center" minH="60vh" bg={colors.grayBg}>
        <Spinner size="xl" color={colors.teal} />
      </Flex>
    );
  }

  if (!news) {
    return (
      <Flex py={20} justify="center" align="center" minH="60vh" bg={colors.grayBg}>
        <Text fontSize="lg" color="gray.600">
          {isArabic ? " الخبر غير موجود" : "The news does not exist"}
        </Text>
      </Flex>
    );
  }

  // Motion wrappers
  const MotionImage = motion(Image);
  const MotionHeading = motion(Heading);
  const MotionBadge = motion(Badge);
  const MotionText = motion(Text);

  return (
    <Box bg={colors.grayBg} py={20} px={6} minH="80vh">
      <Container maxW="4xl">
        <Stack spacing={8}>
          {/* IMAGE */}
          <MotionImage
            src={news.imageUrl || "/placeholder.jpg"}
            borderRadius="xl"
            boxShadow="lg"
            maxH="400px"
            w="100%"
            objectFit="cover"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
          />

          {/* TITLE + DATE */}
          <Stack spacing={3} textAlign="center">
            <MotionHeading
              size="2xl"
              color={colors.teal}
              fontWeight="bold"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {news.title}
            </MotionHeading>
            {news.date && (
              <MotionBadge
                colorScheme="teal"
                fontSize="0.9em"
                alignSelf="center"
                px={4}
                py={1}
                borderRadius="full"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                {new Date(news.date).toLocaleDateString(isArabic ? "ar-SA" : "en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </MotionBadge>
            )}
          </Stack>

          {/* CONTENT */}
          <MotionText
            fontSize={{ base: "md", md: "lg" }}
            color="gray.700"
            lineHeight="taller"
            whiteSpace="pre-line"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.6 }}
          >
            {news.content}
          </MotionText>
        </Stack>
      </Container>
    </Box>
  );
}
